/**
 * Market Context
 * 
 * React Context for accessing market configuration throughout the app.
 * Market config is determined by VITE_MARKET environment variable at build time.
 */

import { createContext, useContext, useMemo, ReactNode } from 'react';

// Import market config - we'll need to create a frontend-compatible version
// For now, we'll define it inline or use a JSON import

interface MarketConfig {
  marketId: string;
  brandName: string;
  domain: string;
  supabase: {
    url: string;
    projectId: string;
  };
  shopify: {
    storeDomain: string;
  };
  cloudflare: {
    pagesProject: string;
    workersProject: string;
    r2Bucket: string;
  };
}

interface MarketContextValue {
  marketConfig: MarketConfig;
  marketId: string;
  brandName: string;
}

const MarketContext = createContext<MarketContextValue | null>(null);

/**
 * Get market config from environment
 * In frontend, we use VITE_MARKET, fallback to 'finland'
 */
function getMarketConfigFromEnv(): MarketConfig {
  const marketId = (import.meta.env.VITE_MARKET || 'finland') as string;
  
  // For frontend, we'll use environment variables directly
  // Market config will be constructed from env vars at build time
  const config: MarketConfig = {
    marketId,
    brandName: marketId === 'bangladesh' ? 'posterden' : 'siella',
    domain: marketId === 'bangladesh' ? 'posterden.bd' : 'siella.fi',
    supabase: {
      url: import.meta.env.VITE_SUPABASE_URL || '',
      projectId: '',
    },
    shopify: {
      storeDomain: import.meta.env.VITE_SHOPIFY_STORE || 
        (marketId === 'bangladesh' ? 'posterden.myshopify.com' : 'siella-maps.myshopify.com'),
    },
    cloudflare: {
      pagesProject: marketId === 'bangladesh' ? 'maps-posterden' : 'maps-siella',
      workersProject: marketId === 'bangladesh' ? 'maps-api-posterden' : 'maps-api-siella',
      r2Bucket: marketId === 'bangladesh' ? 'map-render-output-bangladesh' : 'map-render-output-finland',
    },
  };
  
  return config;
}

interface MarketProviderProps {
  children: ReactNode;
}

export function MarketProvider({ children }: MarketProviderProps) {
  const marketConfig = useMemo(() => getMarketConfigFromEnv(), []);
  
  const value = useMemo(() => ({
    marketConfig,
    marketId: marketConfig.marketId,
    brandName: marketConfig.brandName,
  }), [marketConfig]);
  
  return (
    <MarketContext.Provider value={value}>
      {children}
    </MarketContext.Provider>
  );
}

/**
 * Hook to access market configuration
 */
export function useMarket(): MarketContextValue {
  const context = useContext(MarketContext);
  if (!context) {
    throw new Error('useMarket must be used within MarketProvider');
  }
  return context;
}
